// SpecialSession/useRegistrationMemberOptions.ts
import { useEffect, useState } from 'react';
import { useRegistrationService } from '@actschurch/ccms-sdk';
import type { FieldOption } from '@/components/advanced/FormBuilder/types/BuilderField';

/**
 * The registration's members as choices for 'member' questions (value = memberID as a string).
 *
 * Membership comes from `getMembers()` and names from `getView().participants` — the same split
 * MembersTab uses, because a linked member can be missing from the view's participant list. Those
 * members still appear, labelled by ID, rather than silently dropping out of the dropdown.
 */
export function useRegistrationMemberOptions(registrationID: number): FieldOption[] {
  const registrationService = useRegistrationService();
  const [options, setOptions] = useState<FieldOption[]>([]);

  useEffect(() => {
    if (!registrationID) return;
    let cancelled = false;
    Promise.all([
      registrationService.getMembers(registrationID),
      registrationService.getView(registrationID).catch(() => null),
    ])
      .then(([rawLinks, view]) => {
        if (cancelled) return;
        // Same defensive unwrap as MembersTab: getMembers has been seen returning the raw envelope.
        const links = ((rawLinks as unknown as { data?: unknown })?.data ?? rawLinks) as { memberID: number }[];
        const names = new Map(
          (view?.participants ?? [])
            .filter((p) => p.memberID != null)
            .map((p) => [p.memberID as number, p.fullName || [p.name, p.surname].filter(Boolean).join(' ')]),
        );
        setOptions(
          (Array.isArray(links) ? links : []).map((l) => ({
            value: String(l.memberID),
            label: names.get(l.memberID) || `Member #${l.memberID}`,
          })),
        );
      })
      .catch((e) => console.error('[useRegistrationMemberOptions] failed to load members:', e));
    return () => {
      cancelled = true;
    };
  }, [registrationService, registrationID]);

  return options;
}
